<template>
  <div class="user-page">
    <h2 class="profile-heading">
      {{ $t('profile.profile') }}
      <i class="icon-feather-arrow-right"></i>
      {{ userProfile.name }}
    </h2>

    <div class="profile-layout">
      <!-- Sidebar -->
      <div class="profile-sidebar">
        <div class="profile-card">
          <div class="profile-card__photo">
            <img :src="userProfile.photo_link" :alt="userProfile.name" />
          </div>
          <h4 class="profile-card__name">{{ userProfile.fullname }}</h4>
          <div class="profile-card__actions">
            <button type="button" class="profile-btn profile-btn--primary" @click="openMessageModal">
              <i class="uil-envelope-alt"></i> {{ $t('general.send_message') }}
            </button>
            <button type="button" class="profile-btn profile-btn--outline"
                    @click="$router.push({ name: 'user.wall', query: { user: userProfile.id } })">
              <i class="uil-coffee"></i> {{ $t('general.look_wall') }}
            </button>
          </div>
        </div>
      </div>

      <!-- Main Content -->
      <div class="profile-main">
        <!-- Member Info -->
        <div class="profile-section">
          <div class="profile-section__header">
            <h5 class="profile-section__title">
              <i class="icon-feather-user"></i> {{ $t('profile.member_info') }}
            </h5>
          </div>
          <div class="profile-section__divider"></div>
          <div class="profile-grid">
            <div class="profile-field">
              <h6 class="profile-field__label">{{ $t('profile.name') }}</h6>
              <p class="profile-field__value">{{ userProfile.name + ' ' + userProfile.surname }}</p>
            </div>
            <div class="profile-field">
              <h6 class="profile-field__label">{{ $t('profile.surname') }}</h6>
              <p class="profile-field__value">{{ userProfile.surname }}</p>
            </div>
            <div class="profile-field">
              <h6 class="profile-field__label">{{ $t('profile.dob') }}</h6>
              <p class="profile-field__value">{{ userProfile.dob }}</p>
            </div>
            <div class="profile-field">
              <h6 class="profile-field__label">{{ $t('profile.email') }}</h6>
              <p class="profile-field__value">{{ userProfile.email }}</p>
            </div>
            <div class="profile-field">
              <h6 class="profile-field__label">{{ $t('profile.phone') }}</h6>
              <p class="profile-field__value">{{ userProfile.phone }}</p>
            </div>
          </div>
        </div>

        <!-- Company Info -->
        <div class="profile-section">
          <div class="profile-section__header">
            <h5 class="profile-section__title">
              <i class="icon-feather-briefcase"></i> {{ $t('profile.company_profile') }}
            </h5>
          </div>
          <div class="profile-section__divider"></div>
          <div class="profile-grid">
            <div class="profile-field">
              <h6 class="profile-field__label">{{ $t('profile.company_name') }}</h6>
              <p class="profile-field__value">{{ userProfile && userProfile.company ? userProfile.company.name : '-' }}</p>
            </div>
            <div class="profile-field">
              <h6 class="profile-field__label">{{ $t('profile.position') }}</h6>
              <p class="profile-field__value">{{ userProfile && userProfile.position ? userProfile.position.name : '-' }}</p>
            </div>
            <div class="profile-field">
              <h6 class="profile-field__label">{{ $t('profile.title') }}</h6>
              <p class="profile-field__value">{{ userProfile && userProfile.title ? userProfile.title.name : '-' }}</p>
            </div>
            <div class="profile-field">
              <h6 class="profile-field__label">{{ $t('profile.gm_region') }}</h6>
              <p class="profile-field__value">{{ userProfile && userProfile.region ? userProfile.region.name : '-' }}</p>
            </div>
            <div v-if="$hasPermission('read_branch')" class="profile-field">
              <h6 class="profile-field__label">{{ $t('profile.unit') }}</h6>
              <p class="profile-field__value">{{ userProfile && userProfile.branch ? userProfile.branch.name : '-' }}</p>
            </div>
            <div class="profile-field">
              <h6 class="profile-field__label">{{ $t('profile.branch') }}</h6>
              <p class="profile-field__value">{{ userProfile && userProfile.store ? userProfile.store.name : '-' }}</p>
            </div>
          </div>
        </div>

        <!-- Collections -->
        <div v-if="userProfile.collections && userProfile.collections.length" class="profile-section">
          <div class="profile-section__header">
            <h5 class="profile-section__title">
              <i class="uil-layers"></i> {{ $t('profile.user_collections') }}
            </h5>
          </div>
          <div class="profile-section__divider"></div>
          <div class="profile-collections">
            <div class="uk-position-relative uk-slider uk-slider-container" tabindex="-1" uk-slider="autoplay: true">
              <ul class="uk-slider-items profile-collections__slider uk-grid">
                <template v-for="col in userProfile.collections">
                  <collection
                      :key="col.id"
                      :collection="col"
                      @onClickCard="collectionDetails"
                  />
                </template>
              </ul>
              <div class="uk-flex uk-flex-center mt-3">
                <ul class="uk-slider-nav uk-dotnav"></ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <SendMessage v-if="sendMessageToUserId" :user-id="sendMessageToUserId" @close="handleSendMessageClose" />
  </div>
</template>

<script>
import { BASE_URL, CURRENT_ITEM, GET_USER } from "@/core/services/store/user.module";
import module from "@/core/services/store/user.module";
import moment from "moment";
import store from "@/core/services";
import Collection from "@/view/components/index/Collection";
import SendMessage from "@/view/company/User/SendMessage";

export default {
  name: "UserShow",
  components: { Collection, SendMessage },

  data() {
    return {
      sendMessageToUserId: null,
    };
  },

  methods: {
    collectionDetails(payload) {
      this.$router.push({ name: 'user.collection-detail', params: { id: payload } })
    },
    openMessageModal() {
      if (this.userProfile?.id) {
        this.sendMessageToUserId = this.userProfile.id;
      }
    },
    handleSendMessageClose() {
      this.sendMessageToUserId = null;
    },
  },
  computed: {
    userProfile() {
      let UserShow = store.getters[this.$options.name + '/' + CURRENT_ITEM];

      if (typeof UserShow.user !== "undefined") {
        UserShow.user.dob = UserShow.user.dob ? moment(UserShow.user.dob).format('YYYY-MM-DD') : '-'
        UserShow.user.fullname = UserShow.user.name + ' ' + UserShow.user.surname;

        if (typeof UserShow.collections == "undefined") {
          UserShow.collections = [];
        } else {
          UserShow.user.collections = UserShow.collections;
        }

        return UserShow.user;
      }

      return {};
    }
  },
  beforeCreate() {
    function registerStoreModule(moduleName, storeModule) {
      if (!(store && store.state && store.state[moduleName])) {
        store.registerModule(moduleName, storeModule)
      }
    }
    registerStoreModule(this.$options.name, module)
  },

  mounted() {
    this.$store.dispatch(this.$options.name + '/' + GET_USER, {
      url: BASE_URL + '/show-to-guest/' + this.$route.params.id,
      acceptPromise: true,
    })
  },
}
</script>

<style scoped>
/* Heading */
.profile-heading {
  font-size: 20px;
  font-weight: 600;
  color: #1e293b;
  margin: 0 0 24px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.profile-heading i {
  font-size: 16px;
  color: #94a3b8;
}

/* Layout */
.profile-layout {
  display: flex;
  gap: 24px;
  align-items: flex-start;
}

/* Sidebar */
.profile-sidebar {
  width: 280px;
  min-width: 280px;
  position: sticky;
  top: 24px;
}

.profile-card {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  padding: 28px 20px;
  text-align: center;
}

.profile-card__photo {
  width: 96px;
  height: 96px;
  border-radius: 50%;
  overflow: hidden;
  margin: 0 auto 16px;
  border: 3px solid #e2e8f0;
}

.profile-card__photo img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.profile-card__name {
  font-size: 17px;
  font-weight: 600;
  color: #1e293b;
  margin: 0 0 20px;
}

.profile-card__actions {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

/* Buttons */
.profile-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 10px 16px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  border: 1px solid transparent;
  width: 100%;
}

.profile-btn i {
  font-size: 16px;
}

.profile-btn--primary {
  background: #2563eb;
  color: #fff;
  border-color: #2563eb;
}

.profile-btn--primary:hover {
  background: #1d4ed8;
  border-color: #1d4ed8;
}

.profile-btn--outline {
  background: #fff;
  color: #475569;
  border-color: #e2e8f0;
}

.profile-btn--outline:hover {
  background: #f8fafc;
  border-color: #cbd5e1;
}

/* Main Content */
.profile-main {
  flex: 1;
  min-width: 0;
  max-width: 100%;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

/* Section Card */
.profile-section {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  overflow: hidden;
  width: 100%;
}

.profile-section__header {
  padding: 16px 20px;
}

.profile-section__title {
  font-size: 15px;
  font-weight: 600;
  color: #1e293b;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
}

.profile-section__title i {
  font-size: 17px;
  color: #64748b;
}

.profile-section__divider {
  height: 1px;
  background: #e2e8f0;
}

/* Info Grid */
.profile-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0;
  padding: 4px 0;
}

.profile-field {
  padding: 14px 20px;
}

.profile-field__label {
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  margin: 0 0 4px;
}

.profile-field__value {
  font-size: 14px;
  color: #1e293b;
  margin: 0;
  word-break: break-word;
}

/* Collections */
.profile-collections {
  padding: 20px;
  overflow: hidden;
}

.profile-collections :deep(.uk-grid) {
  margin-left: 0;
}

.profile-collections__slider {
  margin: 0;
  padding: 0;
  list-style: none;
}

.profile-collections__slider > li {
  width: 33.333%;
  padding: 0 8px;
  box-sizing: border-box;
}

/* Responsive - Tablet */
@media (max-width: 960px) {
  .profile-sidebar {
    width: 240px;
    min-width: 240px;
  }

  .profile-card {
    padding: 20px 16px;
  }

  .profile-card__photo {
    width: 80px;
    height: 80px;
  }

  .profile-heading {
    font-size: 18px;
  }
}

/* Responsive - Mobile */
@media (max-width: 768px) {
  .profile-layout {
    flex-direction: column;
    gap: 16px;
  }

  .profile-sidebar {
    width: 100%;
    min-width: 100%;
    position: static;
  }

  .profile-main {
    width: 100%;
    max-width: 100%;
  }

  .profile-section {
    width: 100%;
    max-width: 100%;
    border-radius: 14px;
  }

  .profile-card {
    display: flex;
    align-items: center;
    gap: 16px;
    text-align: left;
    padding: 16px;
    border-radius: 14px;
    width: 100%;
  }

  .profile-card__photo {
    width: 56px;
    height: 56px;
    margin: 0;
    flex-shrink: 0;
    border-width: 2px;
  }

  .profile-card__name {
    font-size: 16px;
    margin: 0 0 8px;
  }

  .profile-card__actions {
    flex-direction: row;
    gap: 8px;
  }

  .profile-btn {
    min-height: 44px;
    padding: 0 16px;
    font-size: 14px;
    border-radius: 10px;
    gap: 4px;
  }

  .profile-btn:hover {
    background: inherit;
  }

  .profile-btn:active {
    transform: scale(0.98);
    opacity: 0.85;
  }

  .profile-btn--primary:active {
    background: #1d4ed8;
  }

  .profile-btn--outline:active {
    background: #f1f5f9;
  }

  .profile-btn i {
    font-size: 16px;
  }

  .profile-heading {
    font-size: 16px;
    margin-bottom: 16px;
  }

  .profile-section__header {
    padding: 16px;
  }

  .profile-section__title {
    font-size: 14px;
  }

  .profile-grid {
    grid-template-columns: 1fr 1fr;
    padding: 0;
  }

  .profile-field {
    padding: 12px 16px;
  }

  .profile-field__label {
    font-size: 12px;
    margin-bottom: 4px;
  }

  .profile-field__value {
    font-size: 14px;
  }

  .profile-collections {
    padding: 16px;
  }

  .profile-collections__slider > li {
    width: 50%;
    padding: 0 8px;
  }
}

/* Responsive - Small Mobile */
@media (max-width: 480px) {
  .profile-layout {
    gap: 12px;
  }

  .profile-card {
    flex-direction: column;
    text-align: center;
    padding: 16px;
  }

  .profile-card__photo {
    width: 64px;
    height: 64px;
  }

  .profile-card__name {
    margin: 0 0 8px;
  }

  .profile-card__actions {
    flex-direction: column;
    width: 100%;
    gap: 8px;
  }

  .profile-btn {
    min-height: 48px;
    font-size: 16px;
  }

  .profile-grid {
    grid-template-columns: 1fr;
  }

  .profile-heading {
    font-size: 16px;
    margin-bottom: 12px;
  }

  .profile-field {
    padding: 8px 16px;
  }

  .profile-collections__slider > li {
    width: 100%;
    padding: 0 4px;
  }
}
</style>
